import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Icon, Skeleton, Tab } from '../../../../components';
import { Sheet, useSheetDismiss } from '../../../../components/Sheet';
import { measureImage, SHARE_IMAGE_MAX_BYTES, SHARE_IMAGE_MIN, uploadShareImage } from '../../share-image';
import type { ImageRef } from '../../seo-types';
import { validImageUrl } from '../../seo-validation';
import { useSiteArtLibrary } from '../../useSiteArtLibrary';
import {
  Body,
  DropHint,
  DropTitle,
  DropZone,
  ErrorText,
  Footer,
  Grid,
  GridItem,
  HiddenInput,
  Note,
  Tabs,
  Thumb,
  WarnText,
} from './ImagePickerSheet.styles';

export type ImagePickerTab = 'upload' | 'library';

export type ImagePickerSheetProps = {
  open: boolean;
  /** The brand's tenant: its website art is the library (none = no library). */
  tenantId?: number;
  /** Stored with an upload. */
  brandName: string;
  /** The image the owner has now (outlined in the library). */
  current?: ImageRef;
  /** Offer "Use site default" (route level only). */
  allowSiteDefault: boolean;
  /** The picked image; undefined = use the site default. */
  onPick: (image: ImageRef | undefined) => void;
  onClose: () => void;
  /** The tab the sheet opens on. */
  initialTab?: ImagePickerTab;
};

type UploadError = 'not-image' | 'too-large' | 'failed' | null;

/** Smaller than the size shares are drawn at: picked anyway, with a warning. Unknown sizes say nothing. */
export const isSmallShareImage = (image: Pick<ImageRef, 'width' | 'height'>): boolean =>
  image.width > 0 && image.height > 0 && (image.width < SHARE_IMAGE_MIN.width || image.height < SHARE_IMAGE_MIN.height);

const UPLOAD_ERROR_COPY: Record<Exclude<UploadError, null>, string> = {
  'not-image': "That file isn't an image",
  'too-large': 'That image is over 5 MB',
  failed: "That couldn't be uploaded. Please try again.",
};

/** The upload tab: a drop zone over a file input, the busy state, the refusals and the small-image warning. */
const UploadTab = ({
  brandName,
  onPick,
  onBusy,
}: {
  brandName: string;
  onPick: (image: ImageRef) => void;
  onBusy: (busy: boolean) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const [over, setOver] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<UploadError>(null);
  const [uploaded, setUploaded] = React.useState<ImageRef | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  // Syncs the in-flight upload with the tab's life: leaving it (the sheet closing) aborts the request.
  React.useEffect(() => () => abortRef.current?.abort(), []);

  const setBusyBoth = (value: boolean): void => {
    setBusy(value);
    onBusy(value);
  };

  const upload = async (file: File): Promise<void> => {
    if (busy) return;
    setUploaded(null);
    if (!file.type.startsWith('image/')) return setError('not-image');
    if (file.size > SHARE_IMAGE_MAX_BYTES) return setError('too-large');
    setError(null);
    const abort = new AbortController();
    abortRef.current = abort;
    setBusyBoth(true);
    try {
      let image = await uploadShareImage(file, brandName, abort.signal);
      if (image.width <= 0 || image.height <= 0) {
        // The store did not say its size: read it from the stored image (unknown stays 0).
        image = await measureImage(image.url).then(
          (size) => ({ ...image, ...size }),
          () => image,
        );
      }
      if (abort.signal.aborted) return;
      setUploaded(image);
      onPick(image);
    } catch (e) {
      if (abort.signal.aborted) return;
      setError(e instanceof Error && e.message === 'too-large' ? 'too-large' : 'failed');
    } finally {
      if (!abort.signal.aborted) setBusyBoth(false);
    }
  };

  const take = (files: FileList | null): void => {
    const file = files?.[0];
    if (file) void upload(file);
  };

  return (
    <Body>
      <DropZone
        $over={over}
        $busy={busy}
        aria-busy={busy}
        onDragOver={(event) => {
          event.preventDefault();
          if (!busy) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          if (!busy) take(event.dataTransfer.files);
        }}
      >
        <HiddenInput
          type="file"
          accept="image/*"
          aria-label={t('Choose an image')}
          disabled={busy}
          onChange={(event) => {
            take(event.currentTarget.files);
            event.currentTarget.value = '';
          }}
        />
        <Icon name="image" size={28} />
        {busy ? (
          <DropTitle text="Uploading…" tag="span" />
        ) : (
          <>
            <DropTitle text="Drop an image here or choose a file" tag="span" />
            <DropHint text="At least 1200×630 looks best. Up to 5 MB." tag="span" />
          </>
        )}
      </DropZone>
      {error ? <ErrorText text={UPLOAD_ERROR_COPY[error]} tag="span" /> : null}
      {uploaded && isSmallShareImage(uploaded) ? (
        <WarnText text="Smaller than 1200×630: it may look soft when shared" tag="span" />
      ) : null}
    </Body>
  );
};

/** The library tab: the brand's website art that can be shared (https only), a pick measured before it lands. */
const LibraryTab = ({
  tenantId,
  current,
  onPick,
}: {
  tenantId?: number;
  current?: ImageRef;
  onPick: (image: ImageRef) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const library = useSiteArtLibrary(tenantId);
  const [measuring, setMeasuring] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);
  const mountedRef = React.useRef(true);
  // Syncs the pending measure with the tab's life: an answer after it closes lands nowhere.
  React.useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // An image that is not https with a host would block Publish (the server's image-url-invalid): never offered.
  const usable = library.urls.filter(validImageUrl);
  const dropped = library.urls.length - usable.length;

  const pick = async (url: string): Promise<void> => {
    if (measuring !== null) return;
    setMeasuring(url);
    setFailed(false);
    try {
      const size = await measureImage(url);
      if (mountedRef.current) onPick({ url, ...size });
    } catch {
      if (mountedRef.current) setFailed(true);
    } finally {
      if (mountedRef.current) setMeasuring(null);
    }
  };

  if (library.status === 'loading') {
    return (
      <Grid aria-busy="true">
        {[0, 1, 2].map((n) => (
          <GridItem key={n}>
            <Skeleton height={64} block />
          </GridItem>
        ))}
      </Grid>
    );
  }
  if (library.status === 'failed') {
    return (
      <Callout
        tone="danger"
        text="Couldn't load your site's images."
        action={<Button label={t('Try again')} variant="outline" icon="refresh" onClick={library.reload} />}
      />
    );
  }
  return (
    <Body>
      {usable.length === 0 ? (
        <Note text="No images from your site's art yet" tag="paragraph" />
      ) : (
        <Grid aria-label={t('Your site images')}>
          {usable.map((url) => (
            <GridItem key={url}>
              <Thumb
                type="button"
                $selected={current?.url === url}
                aria-pressed={current?.url === url}
                aria-label={t('Use this image')}
                disabled={measuring !== null}
                onClick={() => void pick(url)}
              >
                <img src={url} alt="" loading="lazy" />
              </Thumb>
            </GridItem>
          ))}
        </Grid>
      )}
      {dropped > 0 ? (
        <Note text="Some of your site's images aren't served over https, so they can't be shared." tag="paragraph" />
      ) : null}
      {failed ? <ErrorText text="That image couldn't be read. Please pick another one." tag="span" /> : null}
    </Body>
  );
};

const PickerSheet = ({
  tenantId,
  brandName,
  current,
  allowSiteDefault,
  onPick,
  onClose,
  initialTab = 'upload',
}: Omit<ImagePickerSheetProps, 'open'>): React.ReactElement => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<ImagePickerTab>(initialTab);
  const [busy, setBusy] = React.useState(false);
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  const selectTab = (event: React.MouseEvent<HTMLElement>): void => {
    if (busy) return;
    setTab(event.currentTarget.dataset.id === 'library' ? 'library' : 'upload');
  };

  return (
    <Sheet
      title="Share image"
      onClose={onClose}
      closeHandle={closeHandle}
      busy={busy}
      footer={
        allowSiteDefault ? (
          <Footer>
            <Button
              label={t('Use site default')}
              variant="outline"
              disabled={busy}
              onClick={() => {
                if (busy) return;
                onPick(undefined);
                dismiss();
              }}
            />
          </Footer>
        ) : undefined
      }
    >
      <Body>
        <Tabs>
          <Tab id="upload" label={t('Upload')} active={tab === 'upload'} onClick={selectTab} />
          <Tab id="library" label={t('From library')} active={tab === 'library'} onClick={selectTab} />
        </Tabs>
        {tab === 'upload' ? (
          <UploadTab brandName={brandName} onPick={onPick} onBusy={setBusy} />
        ) : (
          <LibraryTab tenantId={tenantId} current={current} onPick={onPick} />
        )}
      </Body>
    </Sheet>
  );
};

/**
 * The share image picker: Upload (drag and drop or a file, up to 5 MB, a warning under 1200×630), From library (the
 * brand's website art, https only), and at route level "Use site default". Mounted only while open, so each opening
 * starts clean and the library is read only when its tab shows.
 */
export const ImagePickerSheet = ({ open, ...rest }: ImagePickerSheetProps): React.ReactElement | null =>
  open ? <PickerSheet {...rest} /> : null;

export default ImagePickerSheet;
